import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Basic error boundary component.
 * Catches JavaScript errors anywhere in its child component tree, logs those errors,
 * and displays a fallback UI instead of the component tree that crashed.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  /**
   * Updates state so the next render will show the fallback UI.
   * @param error - The error that was thrown.
   * @returns The updated state.
   */
  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  /**
   * Logs the error to an error reporting service (or console).
   * @param error - The error that was thrown.
   * @param errorInfo - An object with a componentStack key containing information about which component threw the error.
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can also log the error to an error reporting service
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="bg-canvas text-default flex h-screen w-screen flex-col items-center justify-center">
          <h1 className="mb-4 text-lg font-semibold">Something went wrong.</h1>
          <p className="text-secondary mb-8 text-base">
            An unexpected error occurred. Please try reloading the page.
          </p>
          <button
            onClick={this.handleReload}
            className="bg-fillBrand text-brandOnFill hover:bg-fillBrand/90 rounded-sm px-4 py-2"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
